import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSeller } from "@/hooks/useIsSeller";
import { useSellerOtpStatus } from "@/hooks/useSellerOtpStatus";
import { useSellerAgreementStatus } from "@/hooks/useSellerAgreementStatus";
import { SellerAgreementGate } from "@/components/SellerAgreementGate";

const SellerProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isSeller, loading: sellerLoading } = useIsSeller();
  const { canAccessSellerArea, loading: otpLoading } = useSellerOtpStatus();
  const { needsAgreement, loading: agreementLoading } = useSellerAgreementStatus();
  const location = useLocation();

  // Show loading state while checking authentication, seller status, OTP and agreement
  if (authLoading || sellerLoading || (isSeller && (otpLoading || agreementLoading))) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  // Not logged in - redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  // Not a seller - redirect to account
  if (!isSeller) {
    return <Navigate to="/account" replace />;
  }

  // Seller but needs 2FA (first time, or device not trusted / expired)
  if (!canAccessSellerArea) {
    return <Navigate to="/seller-2fa-setup" replace />;
  }

  // Seller Agreement not accepted (or an older version was accepted) -
  // non-dismissible gate covering every seller route
  if (needsAgreement) {
    return (
      <div className="bg-background">
        <Helmet>
          <title>Seller Agreement | PERLLA</title>
          <meta name="description" content="Accept the PERLLA Seller Agreement to continue selling." />
        </Helmet>
        <div className="p-6">
          <SellerAgreementGate />
        </div>
      </div>
    );
  }

  // All checks passed - render the protected content
  return children;
};

export default SellerProtectedRoute;
